export const DEFAULT_SALES_TAX_RATE = 0.07;

/** Round half-up to cents. Portal payment.breakdown invariants use this. */
export function roundMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Normalize tax-exempt flags from Firestore / forms / integrations.
 * Accepts true, 1, "true", "1" — rejects falsey and the string "false".
 */
export function isTaxExemptFlag(value) {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  return false;
}

/**
 * Whether sales tax should be waived.
 * - invoiceTaxExempt: when true/false, explicit invoice-time decision (wins over customer/order).
 * - Otherwise exempt if customer, order.taxExempt, or order.additionalInstructions.taxExempt.
 *
 * Customer profile tax exempt is treated as a strong default: an invoice override of
 * `false` does NOT win over a tax-exempt customer (avoids sticky taxable invoices).
 * Staff can still uncheck for the current session/PDF; reopening re-applies customer default.
 */
export function resolveTaxExempt({ order, customer = null, invoiceTaxExempt = undefined }) {
  const customerExempt = isTaxExemptFlag(customer?.taxExempt);
  const orderExempt =
    isTaxExemptFlag(order?.taxExempt) ||
    isTaxExemptFlag(order?.additionalInstructions?.taxExempt);

  if (invoiceTaxExempt === true) return true;
  // Don't let a stale invoiceTaxExempt:false keep taxing a tax-exempt customer.
  if (invoiceTaxExempt === false && !customerExempt) return false;

  return orderExempt || customerExempt;
}

/** Snapshot for persisting on order create/update (order-level flags only, not invoice override). */
export function resolveOrderTaxExemptFlag({ order, customer = null }) {
  return resolveTaxExempt({ order, customer, invoiceTaxExempt: undefined });
}

export function feeLineAmount(fee) {
  const quantity = parseFloat(fee?.quantity) || 0;
  const price = parseFloat(fee?.price) || 0;
  return quantity * price;
}

/**
 * Sum fee amounts where the fee is subject to sales tax.
 * `fee.taxable === false` means exempt. Missing flag = taxable (legacy orders).
 */
export function sumTaxableFeesAmount(orderFees) {
  if (!Array.isArray(orderFees)) return 0;
  return orderFees.reduce((sum, fee) => {
    if (fee?.taxable === false) return sum;
    return sum + feeLineAmount(fee);
  }, 0);
}

/**
 * Discount is allocated proportionally: only the taxable slice of (merchandise + fees)
 * is reduced before applying the tax rate.
 */
export function computeSalesTax({
  subtotal,
  feesTotal,
  taxableFeesTotal,
  discountAmount,
  isTaxExempt,
  taxRate = DEFAULT_SALES_TAX_RATE
}) {
  if (isTaxExempt) return 0;
  const preTaxMerchPlusTaxableFees = subtotal + taxableFeesTotal;
  const preTaxFull = subtotal + feesTotal;
  if (preTaxMerchPlusTaxableFees <= 0) return 0;
  if (preTaxFull <= 0) return 0;
  const discountShareOnTaxable = discountAmount * (preTaxMerchPlusTaxableFees / preTaxFull);
  const taxableAmount = Math.max(0, preTaxMerchPlusTaxableFees - discountShareOnTaxable);
  return roundMoney(taxableAmount * taxRate);
}
